import { AxiosResponse } from 'axios';
import apiClient from './apiClient';

export interface LoginDto { email: string; password: string; }
export interface RegisterDto { email: string; password: string; firstName: string; lastName: string; phoneNumber?: string; }

export interface AuthResponse {
  accessToken: string; refreshToken: string; expiresAt: string; user: UserDto;
}

export type MfaRequirement = 'Optional' | 'Required' | 'DisabledByPolicy';
export type MfaStatus = 'NotEnrolled' | 'PendingEnrollment' | 'Enabled' | 'RecoveryRequired';
export type MfaMethod = 'Totp' | 'RecoveryCode';

export interface MfaMethodDto {
  method: MfaMethod;
  displayName: string;
  lastUsedAt?: string;
}

export interface MfaChallengeResponse {
  nextStep: 'MfaRequired' | 'MfaEnrollmentRequired';
  mfaTransactionToken: string;
  expiresAt: string;
  preferredMethod?: MfaMethod;
  availableMethods: MfaMethodDto[];
}

export interface AuthenticatedLoginResponse extends AuthResponse {
  nextStep?: 'Authenticated';
}

export type LoginResponse = AuthenticatedLoginResponse | MfaChallengeResponse;

export const isMfaChallengeResponse = (value: LoginResponse): value is MfaChallengeResponse =>
  value.nextStep === 'MfaRequired' || value.nextStep === 'MfaEnrollmentRequired';

export interface MfaSettingsDto {
  requirement: MfaRequirement;
  status: MfaStatus;
  preferredMethod?: MfaMethod;
  methods: MfaMethodDto[];
  recoveryCodesRemaining: number;
}

export interface TotpEnrollmentDto {
  enrollmentToken: string;
  otpAuthUri: string;
  manualKey: string;
  expiresAt: string;
}

export interface RecoveryCodesDto { recoveryCodes: string[]; }

export interface RequiredMfaEnrollmentResultDto {
  authentication: AuthResponse;
  recoveryCodes: string[];
}

export interface UserDto {
  userId: string; email: string; firstName: string; lastName: string; fullName: string;
  phoneNumber?: string; profileImageUrl?: string; isActive: boolean; roles: string[]; permissions: string[];
  organisations: OrganisationContextDto[];
}

export interface OrganisationContextDto {
  organisationId: string; name: string; isLeadAdmin: boolean; canAccessOrganisationLevel?: boolean;
  subOrganisations: SubOrganisationContextDto[];
}

export interface SubOrganisationContextDto { subOrganisationId: string; name: string; }

let refreshTokenRequest: Promise<AxiosResponse<{ data: AuthResponse }>> | null = null;

const refreshToken = (token?: string) => {
  if (!refreshTokenRequest) {
    refreshTokenRequest = apiClient
      .post<{ data: AuthResponse }>('/api/auth/refresh-token', token ? { refreshToken: token } : {})
      .finally(() => {
        refreshTokenRequest = null;
      });
  }

  return refreshTokenRequest;
};

const authApi = {
  login: (data: LoginDto) => apiClient.post<{ data: LoginResponse }>('/api/auth/login', data),
  verifyMfaChallenge: (data: { transactionToken: string; code: string; useRecoveryCode: boolean }) =>
    apiClient.post<{ data: AuthResponse }>('/api/auth/mfa/challenge/verify', data),
  startRequiredMfaEnrollment: (transactionToken: string) =>
    apiClient.post<{ data: TotpEnrollmentDto }>('/api/auth/mfa/required-enrollment/start', { transactionToken }),
  confirmRequiredMfaEnrollment: (data: { enrollmentToken: string; code: string }) =>
    apiClient.post<{ data: RequiredMfaEnrollmentResultDto }>('/api/auth/mfa/required-enrollment/confirm', data),
  register: (data: RegisterDto) => apiClient.post<{ data: AuthResponse }>('/api/auth/register', data),
  refreshToken,
  logout: () => apiClient.post('/api/auth/logout'),
  getUserContext: () => apiClient.get<{ data: UserDto }>('/api/auth/user-context'),
  updateProfile: (data: { firstName: string; lastName: string; phoneNumber?: string }) =>
    apiClient.put<{ data: UserDto }>('/api/auth/profile', data),
  updateProfileImage: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.put<{ data: UserDto }>('/api/auth/profile-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  removeProfileImage: () => apiClient.delete<{ data: UserDto }>('/api/auth/profile-image'),
  forgotPassword: (email: string) => apiClient.post('/api/auth/forgot-password', { email }),
  resetPassword: (data: { token: string; email: string; newPassword: string }) => apiClient.post('/api/auth/reset-password', data),
  changePassword: (data: { currentPassword: string; newPassword: string }) => apiClient.post('/api/auth/change-password', data),
  getMfaSettings: () => apiClient.get<{ data: MfaSettingsDto }>('/api/auth/mfa'),
  startTotpEnrollment: (currentPassword: string) =>
    apiClient.post<{ data: TotpEnrollmentDto }>('/api/auth/mfa/totp/enrollment', { currentPassword }),
  confirmTotpEnrollment: (data: { enrollmentToken: string; code: string }) =>
    apiClient.post<{ data: RecoveryCodesDto }>('/api/auth/mfa/totp/enrollment/confirm', data),
  setPreferredMfaMethod: (data: { method: MfaMethod; currentPassword: string; code: string }) =>
    apiClient.put('/api/auth/mfa/preferred-method', data),
  regenerateRecoveryCodes: (data: { currentPassword: string; code: string }) =>
    apiClient.post<{ data: RecoveryCodesDto }>('/api/auth/mfa/recovery-codes/regenerate', data),
  disableMfa: (data: { currentPassword: string; code: string }) =>
    apiClient.delete('/api/auth/mfa', { data }),
  googleLogin: () => apiClient.get('/api/auth/google'),
};

export default authApi;
